import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { serializeFormValues, deserializeFormValues } from "@/shared/utils/formSerialization";
import { getMemoryDraft, setMemoryDraft, removeMemoryDraft } from "@/shared/utils/memoryDraftStore";

interface DraftEnvelope {
  schemaVersion: string;
  savedAt: string;
  values: unknown;
}

interface Options<T extends FieldValues, TContext = any, TTransformedValues = T> {
  key: string;
  form: UseFormReturn<T, TContext, TTransformedValues>;
  schemaVersion: string;
  serverUpdatedAt?: string;
  dateFields?: string[];
  ignoreFields?: (keyof T & string)[];
  debounceMs?: number;
  enabled?: boolean;
  storage?: "session" | "memory";
}

interface Result<T extends FieldValues> {
  clearDraft: () => void;
  applyDraft: () => boolean;
  hasValidDraft: boolean;
  draftValues: Partial<T> | null;
}

function readEnvelope(key: string, storage: "session" | "memory"): DraftEnvelope | null {
  try {
    const raw = storage === "memory" ? getMemoryDraft(key) : sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.schemaVersion !== "string" || typeof parsed.savedAt !== "string") return null;
    return parsed as DraftEnvelope;
  } catch {
    return null;
  }
}

function writeEnvelope(key: string, envelope: DraftEnvelope, storage: "session" | "memory"): void {
  const serialized = JSON.stringify(envelope);
  if (storage === "memory") {
    setMemoryDraft(key, serialized);
    return;
  }
  try {
    sessionStorage.setItem(key, serialized);
  } catch (err) {
    if (err instanceof DOMException && (err.name === "QuotaExceededError" || err.code === 22)) {
      evictOldestDraft();
      try {
        sessionStorage.setItem(key, serialized);
      } catch {
        return;
      }
    }
  }
}

function evictOldestDraft(): void {
  try {
    const candidates: { key: string; savedAt: string }[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k == null) continue;
      if (!k.startsWith("draft:") && !k.startsWith("legal:") && !k.startsWith("onboarding:")) continue;
      const envelope = readEnvelope(k, "session");
      if (envelope) candidates.push({ key: k, savedAt: envelope.savedAt });
    }
    candidates.sort((a, b) => a.savedAt.localeCompare(b.savedAt));
    if (candidates.length > 0) {
      sessionStorage.removeItem(candidates[0].key);
    }
  } catch {
    return;
  }
}

export function useFormDraft<T extends FieldValues, TContext = any, TTransformedValues = T>({
  key,
  form,
  schemaVersion,
  serverUpdatedAt,
  dateFields = [],
  ignoreFields = [],
  debounceMs = 500,
  enabled = true,
  storage: storageType = "session",
}: Options<T, TContext, TTransformedValues>): Result<T> {
  const envelope = useMemo(() => (enabled ? readEnvelope(key, storageType) : null), [key, enabled, storageType]);

  const hasValidDraft = useMemo(() => {
    if (!envelope) return false;
    if (envelope.schemaVersion !== schemaVersion) return false;
    if (serverUpdatedAt && envelope.savedAt <= serverUpdatedAt) return false;
    return true;
  }, [envelope, schemaVersion, serverUpdatedAt]);

  const draftValues = useMemo<Partial<T> | null>(() => {
    if (!hasValidDraft || !envelope) return null;
    return deserializeFormValues<T>(envelope.values, dateFields);
  }, [hasValidDraft, envelope, dateFields]);

  const appliedRef = useRef(false);
  const [, forceRerender] = useState(0);

  const dateFieldsRef = useRef(dateFields);
  dateFieldsRef.current = dateFields;

  const applyDraft = useCallback(
    function applyDraft(): boolean {
      if (!hasValidDraft || !envelope) return false;
      const values = deserializeFormValues<T>(envelope.values, dateFieldsRef.current);
      form.reset(values as T);
      appliedRef.current = true;
      forceRerender((n) => n + 1);
      return true;
    },
    [hasValidDraft, envelope, form],
  );

  const clearDraft = useCallback(
    function clearDraft(): void {
      try {
        if (storageType === "memory") {
          removeMemoryDraft(key);
        } else {
          sessionStorage.removeItem(key);
        }
      } catch {
        return;
      }
    },
    [key, storageType],
  );

  useEffect(
    function persistOnChange() {
      if (!enabled) return;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const subscription = form.watch((values) => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          const serialized = serializeFormValues(values, ignoreFields);
          writeEnvelope(key, {
            schemaVersion,
            savedAt: new Date().toISOString(),
            values: serialized,
          }, storageType);
        }, debounceMs);
      });
      return () => {
        if (timer) clearTimeout(timer);
        subscription.unsubscribe();
      };
    },
    [form, key, schemaVersion, ignoreFields, debounceMs, enabled, storageType],
  );

  return {
    clearDraft,
    applyDraft,
    hasValidDraft,
    draftValues,
  };
}
